import { useMemo } from "react";
import { useData } from "@/context/data-provider";
import { calcCompliance } from "@/lib/scoring";
import { PRIO_LABELS, POT_LABELS } from "@/lib/constants";
import { toCsv, downloadCsv } from "@/lib/csv";

export default function Cobertura() {
  const {
    doctors: { doctors },
    visits: { visits },
    period: { period },
  } = useData();

  const data = useMemo(() => {
    return doctors
      .map((d) => ({ doctor: d, compliance: calcCompliance(d, visits, period) }))
      .sort((a, b) => {
        const order = { none: 0, partial: 1, ok: 2 };
        return order[a.compliance.status] - order[b.compliance.status];
      });
  }, [doctors, visits, period]);

  const summary = useMemo(() => {
    const ok = data.filter((d) => d.compliance.status === "ok").length;
    const partial = data.filter((d) => d.compliance.status === "partial").length;
    const none = data.filter((d) => d.compliance.status === "none").length;
    const pct = data.length > 0 ? Math.round((ok / data.length) * 100) : 0;
    return { total: data.length, ok, partial, none, pct };
  }, [data]);

  const daysRemaining = Math.max(0, Math.ceil((new Date(period.end).getTime() - Date.now()) / 86400000));

  const exportCsv = () => {
    const headers = ["Médico", "Local", "Prioridade", "Potencial", "Meta", "Visitas", "Estado"];
    const rows = data.map((d) => [
      d.doctor.name, d.doctor.local, PRIO_LABELS[d.doctor.prio], POT_LABELS[d.doctor.pot],
      String(d.compliance.target), String(d.compliance.actual),
      d.compliance.status === "ok" ? "Cumprido" : d.compliance.status === "partial" ? "Parcial" : "Sem visita",
    ]);
    downloadCsv(`cobertura_${period.start}_${period.end}.csv`, toCsv(headers, rows));
  };

  const compColor = (s: string) => {
    if (s === "ok") return "linear-gradient(90deg,#4A9E2F,#5BAD3E)";
    if (s === "partial") return "linear-gradient(90deg,#D4670A,#E8751A)";
    return "linear-gradient(90deg,#C0392B,#E74C3C)";
  };

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-serif text-[1.3rem]" style={{ color: "var(--mv-ink-dark)" }}>Cobertura</h1>
          <span className="font-mono text-[0.68rem]" style={{ color: "var(--mv-ink-light)" }}>
            {period.start} — {period.end} · {daysRemaining} dias restantes
          </span>
        </div>
        <button className="mv-btn mv-btn-ghost mv-btn-sm" onClick={exportCsv}>📥 CSV</button>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-[11px] mb-[18px]">
        {[
          { title: "Total", num: summary.total, sub: "médicos" },
          { title: "Cumprido", num: summary.ok, sub: `${summary.pct}% meta` },
          { title: "Parcial", num: summary.partial, sub: "em progresso" },
          { title: "Sem Visita", num: summary.none, sub: "sem cobertura" },
        ].map((c) => (
          <div key={c.title} className="stat-card" style={{ borderTop: "none" }}>
            <div className="stat-label">{c.title}</div>
            <div className="stat-number">{c.num}</div>
            <div className="stat-sub">{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="mv-panel">
        <table className="mv-table">
          <thead>
            <tr>
              <th>Médico</th>
              <th className="hidden sm:table-cell">Local</th>
              <th>Prio.</th>
              <th className="hidden md:table-cell">Potencial</th>
              <th>Meta</th>
              <th>Visitas</th>
              <th>Estado</th>
              <th>Progresso</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr><td colSpan={8}><div className="empty-state"><div className="empty-icon">📈</div>Sem médicos</div></td></tr>
            ) : (
              data.map(({ doctor, compliance }) => {
                const pct = Math.min(100, (compliance.actual / compliance.target) * 100);
                return (
                  <tr key={doctor.id}>
                    <td className="font-semibold">{doctor.name}</td>
                    <td className="hidden sm:table-cell" style={{ color: "var(--mv-ink-light)", fontSize: "0.72rem" }}>{doctor.local}</td>
                    <td><span className={`ptag ptag-${doctor.prio}`}>{PRIO_LABELS[doctor.prio]}</span></td>
                    <td className="hidden md:table-cell"><span className={`pottag pottag-${doctor.pot}`}>{POT_LABELS[doctor.pot]}</span></td>
                    <td className="font-mono text-[0.68rem]">{compliance.target}</td>
                    <td className="font-mono text-[0.68rem]">{compliance.actual}</td>
                    <td>
                      <span className={`ctag ctag-${compliance.status === "ok" ? "ok" : compliance.status === "partial" ? "partial" : "none"}`}>
                        {compliance.status === "ok" ? "Cumprido" : compliance.status === "partial" ? "Parcial" : "Sem visita"}
                      </span>
                    </td>
                    <td>
                      <div className="comp-bar">
                        <div className="comp-fill" style={{ width: `${pct}%`, background: compColor(compliance.status) }} />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
